import type { IMediaLibraryModel } from "@models/medialibrary/IMediaLibraryModel";
import type { IProjectModel } from "@models/project/IProjectModel";
import type { IProjectWorkflowModel } from "./IProjectWorkflowModel";
import { getPlayableSteps, type WorkflowDocument } from "./WorkflowDocument";

/**
 * Every distinct `media.url` a run could reach, in STEP ORDER — which is the
 * order to download them in, because the learner reaches step 1 first.
 *
 * Scoped to {@link getPlayableSteps}: a step carrying `disabled: true` is
 * invisible to playback, so its video is bytes nobody will ever watch. That is
 * the same playback scope `WorkflowRunModel` asks every one of its questions
 * through — see `models/workflow/CLAUDE.md`.
 */
export function collectPlayableMediaUrls(
  document: WorkflowDocument | null,
): string[] {
  const urls: string[] = [];
  const seen = new Set<string>();
  for (const step of getPlayableSteps(document)) {
    const url = step.media?.url.trim() ?? "";
    if (url.length === 0 || seen.has(url)) continue;
    seen.add(url);
    urls.push(url);
  }
  return urls;
}

/**
 * `WorkflowMediaPrefetcher` — downloads a workflow's media into the persistent
 * byte cache once the project has finished loading, so a guided run plays its
 * videos from disk rather than waiting on S3 at the step that shows them.
 *
 * **Web-first: there is no Unity counterpart.** Media on a workflow step is a
 * web-only capability.
 *
 * ## ⚠ It waits for `onLoadProjectCompleted`, and that is the whole design
 *
 * The obvious trigger is the workflow document loading — which happens early,
 * off `onActiveProjectChanged`, while the scene's GLBs are still coming down.
 * Starting a 500 MB video there would steal bandwidth from the transfer the
 * load bar is measuring and the user is watching, making the VISIBLE wait
 * longer in order to shorten an invisible one. So the prefetch starts only
 * once the project load has settled, which is still comfortably before anyone
 * presses Play.
 *
 * Either signal may arrive first (a small workflow loads long before a big
 * scene; a project with no assets completes almost at once), so both call
 * {@link maybeStart} and it reads the document fresh.
 *
 * ## The rest of the contract
 *
 * Best-effort throughout: a failure leaves playback to fetch on demand, which
 * is exactly what it did before this existed. Re-runs when the set of urls
 * CHANGES (an author adding a video mid-session gets it warmed before they
 * preview) and not when the document merely changes, so ordinary authoring
 * does not restart a download. Aborted on project switch — the next project's
 * media is what matters by then.
 */
export class WorkflowMediaPrefetcher {
  private readonly m_offFns: Array<() => void> = [];
  private m_projectLoaded = false;
  /** The url set the last run covered; `null` before any run. */
  private m_signature: string | null = null;
  private m_abort: AbortController | null = null;

  constructor(
    private readonly m_project: IProjectModel,
    private readonly m_workflow: IProjectWorkflowModel,
    private readonly m_media: IMediaLibraryModel,
  ) {
    this.m_offFns.push(
      // Fires on a switch AND on unload; both mean the work in flight is for a
      // project nobody is looking at any more.
      this.m_project.onActiveProjectChanged.on(() => this.reset()),
      this.m_project.onLoadProjectCompleted.on(() => {
        this.m_projectLoaded = true;
        this.maybeStart();
      }),
      this.m_workflow.onWorkflowChanged.on(() => this.maybeStart()),
    );
  }

  dispose(): void {
    this.reset();
    for (const off of this.m_offFns) off();
    this.m_offFns.length = 0;
  }

  private reset(): void {
    this.m_abort?.abort();
    this.m_abort = null;
    this.m_projectLoaded = false;
    this.m_signature = null;
  }

  private maybeStart(): void {
    if (!this.m_projectLoaded) return;

    const urls = collectPlayableMediaUrls(this.m_workflow.loadedWorkflow);
    // Keyed on the urls rather than on the document: an author editing prose
    // must not restart a download, while adding or re-pointing a media block
    // must. `\n` cannot appear in a url, so this cannot collide.
    const signature = urls.join("\n");
    if (signature === this.m_signature) return;
    this.m_signature = signature;

    this.m_abort?.abort();
    this.m_abort = null;
    if (urls.length === 0) return;

    const controller = new AbortController();
    this.m_abort = controller;
    void this.m_media.prefetchAsync(urls, controller.signal).catch((err) => {
      if (controller.signal.aborted) return;
      console.warn("[WorkflowMediaPrefetcher] prefetch failed:", err);
    });
  }
}

export const WORKFLOW_MEDIA_PREFETCHER = Symbol.for("Campfire.WorkflowMediaPrefetcher");
